/**
 * Description normalization and merchant extraction for recurring transaction detection
 */

import { DETECTION_CONFIG } from './config';

/**
 * Normalize a description for pattern matching
 * Removes numbers, dates, and special transaction IDs
 *
 * @example
 * normalizeDescription('SPOTIFY 877-778-1161 NY') => 'spotify ny'
 * normalizeDescription('Netflix.com 408-5403700 CA') => 'netflix com ca'
 */
export function normalizeDescription(description: string): string {
  const config = DETECTION_CONFIG.normalization;

  return description
    .toLowerCase()
    .replace(config.longNumberPattern, '') // Remove long numbers (transaction IDs)
    .replace(config.shortNumberPattern, '') // Remove short numbers
    .replace(config.datePattern, '') // Remove dates
    .replace(config.specialCharsPattern, ' ') // Replace special chars with spaces
    .replace(config.multiSpacePattern, ' ') // Collapse multiple spaces
    .trim();
}

/**
 * Merchant name extraction result with multiple matching strategies
 */
export interface MerchantName {
  /** Primary merchant identifier (first significant word) */
  primary: string;

  /** Secondary identifier (first 2 words) */
  secondary: string;

  /** Full identifier (first 3 words) */
  full: string;

  /** Original normalized description */
  normalized: string;
}

/**
 * Extract merchant name from normalized description
 * Returns multiple levels of specificity for flexible matching
 *
 * @example
 * extractMerchant('amazon prime video') => {
 *   primary: 'amazon',
 *   secondary: 'amazon prime',
 *   full: 'amazon prime video',
 *   normalized: 'amazon prime video'
 * }
 */
export function extractMerchant(normalized: string): MerchantName {
  const words = normalized
    .split(' ')
    .filter(w => w.length > 2); // Filter out very short words like "ny", "ca"

  const maxWords = DETECTION_CONFIG.thresholds.maxMerchantWords;
  const significantWords = words.slice(0, maxWords);

  return {
    primary: significantWords[0] || '',
    secondary: significantWords.slice(0, 2).join(' '),
    full: significantWords.join(' '),
    normalized
  };
}

/**
 * Check if a description should be skipped (too generic)
 *
 * @example
 * shouldSkipDescription('payment') => true
 * shouldSkipDescription('netflix subscription') => false
 */
export function shouldSkipDescription(normalized: string): boolean {
  const minLength = DETECTION_CONFIG.thresholds.minMerchantLength;
  const skipList = DETECTION_CONFIG.normalization.skipDescriptions;

  // Too short
  if (normalized.length < minLength) {
    return true;
  }

  // Exact match with skip list
  if (skipList.includes(normalized)) {
    return true;
  }

  return false;
}

/**
 * State for merchant grouping
 * Maps primary merchant name to their normalized group key
 */
export type MerchantGroupState = Map<string, string>;

/**
 * Create a new merchant group state
 */
export function createMerchantGroupState(): MerchantGroupState {
  return new Map();
}

/**
 * Get or create a group key for a merchant
 * Uses progressive matching: primary → secondary → full → normalized
 *
 * @param state - Current grouping state
 * @param merchant - Merchant name extracted from description
 * @returns Group key to use for this transaction
 */
export function getGroupKey(
  state: MerchantGroupState,
  merchant: MerchantName
): string {
  // Strategy 1: Try primary merchant name
  if (merchant.primary) {
    const existingGroup = state.get(merchant.primary);
    if (existingGroup) {
      return existingGroup;
    }

    // No existing group, register this normalized description
    state.set(merchant.primary, merchant.normalized);
    return merchant.normalized;
  }

  // Fallback: use full normalized description
  return merchant.normalized;
}

/**
 * Check if two merchants likely represent the same entity
 * Useful for validating grouping decisions
 *
 * @param merchant1 - First merchant to compare
 * @param merchant2 - Second merchant to compare
 * @returns true if merchants are likely the same entity
 */
export function areMerchantsSimilar(
  merchant1: MerchantName,
  merchant2: MerchantName
): boolean {
  // Same primary = very likely the same
  if (merchant1.primary && merchant1.primary === merchant2.primary) {
    return true;
  }

  // Same secondary = likely the same
  if (merchant1.secondary && merchant1.secondary === merchant2.secondary) {
    return true;
  }

  return false;
}
